import React, { useState, useEffect, useCallback } from 'react';
import MultiSelectModal from '../../Modal/MultiSelectModal';
import { corporateUserService } from '../../../api/services/CorporateUserService';
import { personalUserService } from '../../../api/services/PersonalUserService';
import { adminPanelService } from '../../../api/services/AdminPanelService';
import type { CorporateUserViewModel } from '../../../api/types/corporate-user';
import type { PersonalUserViewModel } from '../../../api/types/personal-user';
import '@fortawesome/fontawesome-free/css/all.min.css';

export type UserRoleFilter = 'personal' | 'corporate';

type UserListItem = (CorporateUserViewModel | PersonalUserViewModel) & { id: string; isDeleted?: boolean };

function getDisplayName(u: UserListItem): string {
  const first = u.firstName?.trim();
  const last = u.lastName?.trim();
  if (first || last) return [first, last].filter(Boolean).join(' ').trim();
  if (u.email) return u.email;
  return u.id;
}

const UserList: React.FC = () => {
  const [roleFilter, setRoleFilter] = useState<UserRoleFilter>('personal');
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserListItem | null>(null);
  const [sendActivationTarget, setSendActivationTarget] = useState<UserListItem | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  const loadUsers = useCallback(() => {
    setLoading(true);
    const promise =
      roleFilter === 'corporate'
        ? corporateUserService.getAll({ pagination: { pageSize: 100, pageNumber: 1 } })
        : personalUserService.getAll({ pagination: { pageSize: 100, pageNumber: 1 } });
    promise
      .then((res) => {
        if (!res.isSuccess || !res.data) {
          setUsers([]);
          return;
        }
        const raw = res.data as UserListItem[] | { items?: UserListItem[] };
        const list = Array.isArray(raw) ? raw : (raw?.items ?? []);
        setUsers(list);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const res = await adminPanelService.deleteUser(deleteTarget.id);
    setDeleteTarget(null);
    setMenuOpenId(null);
    if (res.isSuccess) {
      setActionMessage({ type: 'success', text: 'Kullanıcı silindi.' });
      loadUsers();
    } else {
      setActionMessage({ type: 'danger', text: res.message || 'Silme işlemi başarısız.' });
    }
  };

  const handleConfirmSendActivation = async () => {
    if (!sendActivationTarget) return;
    const res = await adminPanelService.sendActivationEmail(sendActivationTarget.id);
    setSendActivationTarget(null);
    setMenuOpenId(null);
    if (res.isSuccess) {
      setActionMessage({ type: 'success', text: 'Aktivasyon e-postası gönderildi.' });
    } else {
      setActionMessage({ type: 'danger', text: res.message || 'Gönderim başarısız.' });
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Kullanıcı Listesi</h6>
        <select
          className="form-select form-select-sm w-auto"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as UserRoleFilter)}
        >
          <option value="personal">Bireysel</option>
          <option value="corporate">Kurumsal</option>
        </select>
      </div>
      <div className="card-body p-0">
        {actionMessage && (
          <div className={`alert alert-${actionMessage.type} py-2 mb-0 rounded-0`} role="alert">
            {actionMessage.text}
          </div>
        )}
        {loading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        ) : (
          <ul className="list-group list-group-flush">
            {users.map((user) => (
              <li
                key={user.id}
                className={`list-group-item d-flex justify-content-between align-items-center ${user.isDeleted ? 'text-muted' : ''}`}
              >
                <div>
                  <span className="fw-medium">{getDisplayName(user)}</span>
                  {user.email && (
                    <small className="text-muted d-block">{user.email}</small>
                  )}
                  {user.isDeleted && (
                    <span className="badge bg-secondary mt-1">Silindi</span>
                  )}
                </div>
                <div className="dropdown">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-secondary p-0"
                    id={`user-menu-${user.id}`}
                    data-bs-toggle="dropdown"
                    aria-expanded={menuOpenId === user.id}
                    onClick={() => setMenuOpenId(menuOpenId === user.id ? null : user.id)}
                  >
                    <i className="fas fa-ellipsis-v" />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={`user-menu-${user.id}`}>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item text-primary"
                        onClick={() => {
                          setMenuOpenId(null);
                          setSendActivationTarget(user);
                        }}
                      >
                        Aktivasyon E-postası Gönder
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item text-danger"
                        onClick={() => {
                          setMenuOpenId(null);
                          setDeleteTarget(user);
                        }}
                      >
                        Sil
                      </button>
                    </li>
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        )}
        {!loading && users.length === 0 && (
          <div className="text-muted text-center py-4">Kayıt bulunamadı.</div>
        )}
      </div>
      {deleteTarget && (
        <MultiSelectModal
          title="Kullanıcıyı silmek istediğinize emin misiniz?"
          message={getDisplayName(deleteTarget)}
          onAccept={handleConfirmDelete}
          onReject={() => { setDeleteTarget(null); setMenuOpenId(null); }}
        />
      )}
      {sendActivationTarget && (
        <MultiSelectModal
          title="Aktivasyon e-postası gönderilsin mi?"
          message={getDisplayName(sendActivationTarget) + (sendActivationTarget.email ? ` (${sendActivationTarget.email})` : '')}
          onAccept={handleConfirmSendActivation}
          onReject={() => { setSendActivationTarget(null); setMenuOpenId(null); }}
        />
      )}
    </div>
  );
};

export default UserList;
